import type {
  CTAContent,
  FieldlessCTAContent,
} from '@/components/Elements/CTA';
import {
  ContentStyle,
  type FieldlessDropdownMenu,
  type GlobalNavContent,
  type PromoStateConstraintsList,
} from '@/contentful/types';
import type { userLocationInterface, userLocationType } from '@/hooks/geo';
import useViewPortSize from '@/hooks/useViewportSize';
import { getStateConstraintToApply } from '@/utils/constraint';

import DesktopNav from './DesktopNav/DesktopNav';
import MobileNav from './MobileNav/MobileNav';

export interface GlobalNavigationProps extends userLocationInterface {
  categories: FieldlessDropdownMenu[];
  promoCta?: FieldlessCTAContent;
  loginCta: FieldlessCTAContent;
  primaryCta: FieldlessCTAContent;
  secondaryCta?: FieldlessCTAContent;
  style: ContentStyle;
  immersive?: boolean;
}

export enum NavPosition {
  FIXED = 'fixed',
  STATIC = 'static',
  ABSOLUTE = 'absolute',
}

export const getPosition = (navState: NavPosition) => {
  switch (navState) {
    case NavPosition.ABSOLUTE:
      return 'absolute';
    case NavPosition.FIXED:
      return 'fixed';
    default:
      return 'static';
  }
};

export const GlobalNavigationWrapper = ({
  content,
  userLocation,
  setUserLocation,
}: {
  content: GlobalNavContent;
  userLocation: userLocationType | null;
  setUserLocation: (userLocation?: userLocationType) => void;
}) => {
  const categories: FieldlessDropdownMenu[] = content.fields.categories.map(
    (category) => ({
      links: category.fields.links.map((link) => ({
        ...link.fields,
      })),
      name: category.fields.name,
      featured: { ...category.fields.featured.fields },
    })
  );

  // biome-ignore lint/suspicious/noImplicitAnyLet: Automated migration to Biome
  let promoCta;
  if (content.fields?.promoCta) {
    if ('constraints' in content.fields.promoCta.fields) {
      const promoCTAfields = (
        content.fields.promoCta as PromoStateConstraintsList
      ).fields;
      const { default: defaultValue, constraints } = promoCTAfields;
      promoCta = getStateConstraintToApply({
        userLocation,
        defaultValue,
        constraints,
      }) as CTAContent;
      promoCta = promoCta ? { ...promoCta.fields } : undefined;
    } else {
      promoCta = { ...content.fields.promoCta.fields };
    }
  }

  const secondaryCta = content.fields?.secondaryCta
    ? { ...content.fields.secondaryCta.fields }
    : undefined;

  const props: GlobalNavigationProps = {
    categories,
    promoCta,
    secondaryCta,
    loginCta: { ...content.fields.loginCta.fields },
    primaryCta: { ...content.fields.primaryCta.fields },
    style: content.fields.style,
    immersive: content.fields.immersive,
    userLocation: userLocation,
    setUserLocation: setUserLocation,
  };

  return <GlobalNav {...props} />;
};

const GlobalNav = (props: GlobalNavigationProps) => {
  const { isExtraSmall, isSmall } = useViewPortSize();
  const isMobile = isExtraSmall || isSmall;
  const isOnDark = props.style === ContentStyle.OnDark;

  return (
    <header>
      {/** biome-ignore lint/complexity/noUselessFragments: Automated migration to Biome */}
      <>
        {isMobile ? (
          <MobileNav {...props} isOnDark={isOnDark} />
        ) : (
          <DesktopNav {...props} isOnDark={isOnDark} />
        )}
      </>
    </header>
  );
};

export default GlobalNav;
